import { useState } from "react";
import {
  Menu,
  X,
  ChevronRight,
  Sparkles,
  Zap,
  Info,
  Star,
  Lightbulb,
  ShieldCheck,
} from "lucide-react";
import { Button } from "./ui/button";
import { useLogoAnimation } from "./hooks/useLogoAnimation";
import { ContactDialog } from "./ContactDialog";
import { ThemeSwitcher } from "./ThemeSwitcher";

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [dialogConfig, setDialogConfig] = useState({
    title: "Get Started with Kartly",
    description:
      "Transform your business with AI-powered eCommerce solutions. Let's discuss how we can help you scale.",
    buttonText: "Get Started",
  });
  const { isScrolled } = useLogoAnimation();

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setIsMenuOpen(false);
    }
  };

  const handleGetStarted = () => {
    setDialogConfig({
      title: "Get Started with Kartly",
      description:
        "Ready to transform your business with AI-powered eCommerce? Let's start with a personalized consultation to understand your needs.",
      buttonText: "Get Started",
    });
    setContactDialogOpen(true);
    setIsMenuOpen(false);
  };

  const handleTryFree = () => {
    setDialogConfig({
      title: "Try Kartly Free",
      description:
        "Experience the power of intelligent eCommerce with our free trial. No commitment required - see the results for yourself.",
      buttonText: "Start Free Trial",
    });
    setContactDialogOpen(true);
    setIsMenuOpen(false);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
          isScrolled
            ? "glass-effect-strong border-b border-red-100 dark:border-red-800"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo Space Reserved for Animated Logo */}
            <div className="flex items-center">
              <div className={`header-logo-space ${isScrolled ? "show" : ""}`}>
                {/* This space is filled by the AnimatedLogo component */}
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              {[
                { label: "Home", id: "home", icon: Star },
                { label: "About", id: "about", icon: Info },
                { label: "Features", id: "features", icon: Sparkles },
                { label: "Innovation", id: "innovation", icon: Lightbulb },
                { label: "Promise", id: "promise", icon: ShieldCheck },
              ].map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="group relative flex items-center space-x-2 text-primary-light hover:text-red-600 dark:hover:text-red-400 transition-colors duration-300 py-2 px-3 rounded-xl hover:bg-red-50 dark:hover:bg-red-950/50"
                  style={{ "--stagger-delay": index } as any}
                >
                  <item.icon
                    size={16}
                    className="opacity-60 group-hover:opacity-100 transition-opacity"
                  />
                  <span className="font-medium">{item.label}</span>
                  <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-red-500 to-red-700 transition-all duration-300 group-hover:w-full rounded-full"></div>
                </button>
              ))}
            </nav>

            {/* CTA Buttons */}
            <div className="hidden md:flex items-center space-x-4">
              {/* <Button
                variant="outline"
                onClick={handleGetStarted}
                className="border-purple-200 text-purple-600 hover:bg-purple-50 dark:border-purple-400 dark:text-purple-400 dark:hover:bg-purple-950/50 transition-all duration-300 hover:scale-105"
              >
                Get Started
              </Button> */}
              <Button
                onClick={handleTryFree}
                className="btn-interactive bg-gradient-to-r from-red-500 to-orange-500 text-white hover:from-red-600 hover:to-orange-600 shadow-xl hover:shadow-red-500/25 transition-all duration-700 hover:scale-105"
              >
                Get Started
                <ChevronRight size={16} className="ml-2" />
              </Button>
              {/* Theme Switcher - Extreme Right */}
              <div className="ml-6 pl-4 border-l border-red-100 dark:border-red-800">
                <ThemeSwitcher />
              </div>
            </div>

            {/* Mobile Menu Button and Theme Switcher */}
            <div className="md:hidden flex items-center space-x-4">
              <ThemeSwitcher />
              <div className="w-px h-6 bg-red-200 dark:bg-red-700"></div>
              <button
                className="p-2 rounded-xl glass-effect-light hover:bg-red-50 dark:hover:bg-red-950/50 transition-all duration-300"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              >
                {isMenuOpen ? (
                  <X size={24} className="text-primary-light" />
                ) : (
                  <Menu size={24} className="text-primary-light" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div
            className="md:hidden glass-effect-strong border-t border-red-100 dark:border-red-800 animate-slideDown"
            style={{ animation: "slideDown 350ms cubic-bezier(0.4,0,0.2,1)" }}
          >
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <nav className="space-y-4">
                {[
                  { label: "Home", id: "home", icon: Star },
                  { label: "About", id: "about", icon: Info },
                  { label: "Features", id: "features", icon: Sparkles },
                  { label: "Innovation", id: "innovation", icon: Lightbulb },
                  { label: "Promise", id: "promise", icon: ShieldCheck },
                ].map((item, index) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className="block w-full text-left px-4 py-3 rounded-xl text-primary-light hover:bg-red-50 dark:hover:bg-red-950/50 transition-colors duration-300 font-medium flex items-center space-x-3"
                    style={{ animationDelay: `${index * 40}ms` }}
                  >
                    <item.icon size={18} className="opacity-60 mr-2" />
                    <span>{item.label}</span>
                  </button>
                ))}
                <div className="pt-4 border-t border-red-100 dark:border-red-800 space-y-3">
                  {/* <Button
                    variant="outline"
                    onClick={handleGetStarted}
                    className="w-full border-purple-200 text-purple-600 hover:bg-purple-50 dark:border-purple-400 dark:text-purple-400 dark:hover:bg-purple-950/50"
                  >
                    Get Started
                  </Button> */}
                  <Button
                    onClick={handleTryFree}
                    className="w-full bg-gradient-to-r from-red-500 to-orange-500 text-white hover:from-red-600 hover:to-orange-600 shadow-xl hover:shadow-red-500/25 transition-all duration-700 hover:scale-105"
                  >
                    Get Started
                  </Button>
                </div>
              </nav>
            </div>
          </div>
        )}
      </header>

      {/* Contact Dialog */}
      <ContactDialog
        isOpen={contactDialogOpen}
        onClose={() => setContactDialogOpen(false)}
        title={dialogConfig.title}
        description={dialogConfig.description}
        buttonText={dialogConfig.buttonText}
      />
    </>
  );
}
